import React, { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react';
import { useStores } from '../../../hooks/use-stores';
import EmployeesCheckboxTableImpl from '../../users/components/EmployeesCheckboxTableImpl';
import { CheckboxInput, FlexDiv, Heading3 } from '@awarego-ui/components';
import SelectAll from '../../users/components/SelectAll';
import { caseAndDiacriticsInsensitiveSort } from '../../../components/table/table-helpers';
import { expandUserEmail, expandUserName } from '../../../utils/helpers';

function makeColumns(selectable, selection) {
    return [
        {
            Header: 'Name',
            id: 'name',
            accessor: (x) => {
                // scorm name  is formated as "id|[name]|[email]"
                // so check if its  scorm name and parse
                return expandUserName(x.name || x.email);
            },
            sortType: caseAndDiacriticsInsensitiveSort,
            Cell: observer((x) => {
                return selectable ? (
                    <CheckboxInput
                        label={x.value}
                        checked={
                            selection.effectiveSelectionOnCurrentPage[
                                x.row.original.email
                            ]
                        }
                        noYPadding={true} //Removes Y padding and margin, making it fit better into table row
                        onChange={(e) => {
                            selection.toggleRow(
                                x.row.original,
                                e.target.checked
                            );
                        }}
                    />
                ) : (
                    x.value
                );
            }),
        },
        {
            Header: 'Email',
            accessor: (x) => {
                // scorm name  is formated as "id|[name]|[email]"
                // so check if its  scorm name and parse
                return expandUserEmail(x.name, x.email);
            },
        },
    ];
}

function PreviewSyncLists({
    automationId,
    companyId,
    selectable,
    syncStore,
}) {
    const { automationStore } = useStores();
    const store = syncStore || automationStore;
    const pageSize = 3;

    useEffect(() => {
        store.loadPreviewSyncLists(companyId, automationId);
    }, []);

    const { loadingPreviewSyncLists } = store;

    const columnsAdd = useMemo(
        () => makeColumns(selectable, store.previewUsersToAddSelection),
        [store.previewUsersToAddSelection]
    );
    const columnsRemove = useMemo(
        () => makeColumns(selectable, store.previewUsersToRemoveSelection),
        [store.previewUsersToRemoveSelection]
    );

    const onChangePagingParams = (key) => (pageIndex, pageSize) => {
        switch (key) {
            case 'add':
                store.setToAddPageData(pageIndex, pageSize);
                break;
            case 'remove':
                store.setToRemovePageData(pageIndex, pageSize);
                break;
        }
    };

    if (loadingPreviewSyncLists == null) return;
    if (loadingPreviewSyncLists === true) {
        return (
            <FlexDiv px={8} py={24} alignCenter justifyCenter>
                <Heading3>Loading...</Heading3>
            </FlexDiv>
        );
    }

    if (
        loadingPreviewSyncLists === false &&
        store.previewUsersToAdd.length === 0 &&
        store.previewUsersToRemove.length === 0
    ) {
        return (
            <FlexDiv px={8} py={24} alignCenter justifyCenter>
                <Heading3>Users Lists are up to date </Heading3>
            </FlexDiv>
        );
    }

    return (
        <div>
            <FlexDiv column gap={2}>
                <FlexDiv column>
                    <Heading3>New Users</Heading3>
                    {selectable && (
                        <SelectAll
                            selection={store.previewUsersToAddSelection}
                            usersTotal={store.previewUsersToAdd.length}
                            pageSize={5}
                            loadingUsersList={false}
                        />
                    )}
                    <EmployeesCheckboxTableImpl
                        store={store}
                        columns={columnsAdd}
                        usersList={store.previewUsersToAddCurrentPage}
                        usersTotal={store.previewUsersToAdd.length}
                        onChangePagingParams={onChangePagingParams('add')}
                        hidePageSizeSelector={true}
                        pageSize={pageSize}
                        loadingUsersList={false}
                        rowsPerPage={pageSize}
                    />
                </FlexDiv>
                <FlexDiv column>
                    <Heading3>Obsolete Users</Heading3>

                    {selectable && (
                        <SelectAll
                            selection={store.previewUsersToRemoveSelection}
                            usersTotal={store.previewUsersToRemove.length}
                            pageSize={pageSize}
                            loadingUsersList={false}
                        />
                    )}
                    <EmployeesCheckboxTableImpl
                        store={store}
                        columns={columnsRemove}
                        usersList={store.previewUsersToRemoveCurrentPage}
                        usersTotal={store.previewUsersToRemove.length}
                        onChangePagingParams={onChangePagingParams('remove')}
                        hidePageSizeSelector={true}
                        pageSize={pageSize}
                        loadingUsersList={false}
                        rowsPerPage={pageSize}
                    />
                </FlexDiv>
            </FlexDiv>
        </div>
    );
}

export default observer(PreviewSyncLists);
